import { Map } from '@styled-icons/boxicons-solid'
import { ErrorMessage, Form, Formik, useFormikContext } from 'formik'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'

import { INITIAL_LOCATION_VALUES } from '../../constants/form'
import {
  addNewLocation,
  editExistingLocation,
  saveLocationFormValues,
} from '../../redux/locationSlice'
import { pathWithCurrentView } from '../../utils/appUrl'
import {
  formToLocation,
  formToReview,
  isEmptyReview,
  isTooClose,
  locationToForm,
} from '../../utils/form'
import { useAppHistory } from '../../utils/useAppHistory'
import { useIsDesktop } from '../../utils/useBreakpoint'
import { formatMonth } from '../entry/textFormatters'
import Button from '../ui/Button'
import FormButtons from '../ui/FormButtons'
import IconBesideText from '../ui/IconBesideText'
import Label from '../ui/Label'
import LoadingIndicator from '../ui/LoadingIndicator'
import PositionEditIcon from '../ui/PositionEditIcon'
import SubmitButton from '../ui/SubmitButton'
import { Checkbox, Recaptcha, Select, Textarea } from './FormikWrappers'
import { StyledForm } from './FormLayout'
import NotSignedInClickthrough from './NotSignedInClickthrough'
import { ReviewStep } from './ReviewForm'
import SetInitialValue from './SetInitialValue'
import TypesSelect from './TypesSelect'

const StyledPositionFieldLink = styled(Link)`
  color: ${({ theme }) => theme.orange};
  text-decoration: none;
`

const CheckboxLabel = styled.label`
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.tertiaryText};
  margin-block-start: 1em;
`

const ErrorText = styled.p`
  color: ${({ theme }) => theme.red};
  font-size: 0.85rem;
`

const InlineDivider = styled.span`
  margin-inline: 0.75em;
`
const InlineSelects = styled.div`
  display: flex;
  align-items: center;
`

const PositionFieldLink = ({ lat, lng, editingId }) => {
  const { values } = useFormikContext()
  const dispatch = useDispatch()
  const to = editingId
    ? `/locations/${editingId}/edit/position`
    : '/locations/init'
  return (
    <StyledPositionFieldLink
      onClick={() => {
        dispatch(saveLocationFormValues(values))
      }}
      to={pathWithCurrentView(to)}
    >
      <PositionField editable lat={lat} lng={lng} editingId={editingId} />
    </StyledPositionFieldLink>
  )
}

const PositionField = ({ lat, lng, editingId, editable }) => {
  const { locations } = useSelector((state) => state.map)
  const { position } = useSelector((state) => state.location)
  const { t } = useTranslation()
  const tooClose = lat && lng && isTooClose({ lat, lng }, locations, editingId)
  const { setFieldError, setTouched, setFieldValue, errors, touched } =
    useFormikContext()
  const positionTouched = !!touched.position

  // Set position as touched on mount
  useEffect(() => {
    setTouched({ position: true })
  }, [setTouched])

  // Update formik value when position changes in Redux
  useEffect(() => {
    if (position?.lat && position?.lng) {
      setFieldValue('position', { lat: position.lat, lng: position.lng })
    }
  }, [position, setFieldValue])

  // Handle validation for position
  useEffect(() => {
    if (tooClose) {
      setFieldError('position', t('locations.init.position_too_close'))
    } else {
      setFieldError('position', undefined)
    }
  }, [
    positionTouched,
    tooClose,
    lat,
    lng,
    errors.position,
    touched.position,
    setFieldError,
    t,
  ])

  return (
    <>
      <IconBesideText tabIndex={0}>
        {editable ? <PositionEditIcon /> : <Map size={20} />}
        <p className="small" dir="ltr">
          {lat && lng ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : ''}
        </p>
      </IconBesideText>

      <ErrorMessage name="position" component={ErrorText} />
    </>
  )
}

const PositionFieldReadOnly = (props) => (
  <PositionField editable={false} {...props} />
)

const LocationStep = ({ lat, lng, isDesktop, editingId, isLoading }) => {
  const { i18n, t } = useTranslation()
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    label: formatMonth(i, i18n.language),
    value: i,
  }))
  const propertyAccessOptions = [
    {
      label: t('locations.infowindow.access_mode.0'),
      value: 0,
    },
    {
      label: t('locations.infowindow.access_mode.1'),
      value: 1,
    },
    {
      label: t('locations.infowindow.access_mode.2'),
      value: 2,
    },
    {
      label: t('locations.infowindow.access_mode.3'),
      value: 3,
    },
    {
      label: t('locations.infowindow.access_mode.4'),
      value: 4,
    },
  ]
  return (
    <>
      <TypesSelect />
      <Label>{t('locations.form.position')}</Label>
      {isLoading ? (
        <LoadingIndicator />
      ) : isDesktop ? (
        <PositionFieldReadOnly lat={lat} lng={lng} editingId={editingId} />
      ) : (
        <PositionFieldLink lat={lat} lng={lng} editingId={editingId} />
      )}
      <Textarea
        name="description"
        label={t('glossary.description')}
        placeholder={t('locations.form.description_subtext')}
      />
      <Select
        name="access"
        label={t('locations.form.access')}
        options={propertyAccessOptions}
        toFormikValue={(x) => x?.value}
        fromFormikValue={(x) =>
          propertyAccessOptions.find((o) => o.value === x)
        }
        clearable
      />
      <Label>{t('locations.form.season')}</Label>
      <InlineSelects>
        <Select
          name="season_start"
          options={monthOptions}
          toFormikValue={(x) => x?.value}
          fromFormikValue={(x) => monthOptions.find((o) => o.value === x)}
          clearable
        />
        <InlineDivider>
          {t('locations.form.season_start_to_stop_short')}
        </InlineDivider>
        <Select
          name="season_stop"
          options={monthOptions}
          toFormikValue={(x) => x?.value}
          fromFormikValue={(x) => monthOptions.find((o) => o.value === x)}
          clearable
        />
      </InlineSelects>
      <CheckboxLabel>
        <Checkbox id="unverified" label={t('glossary.unverified')} />
        {t('glossary.unverified')}
      </CheckboxLabel>
    </>
  )
}

const validateReviewStep = (review) => {
  const errors = {}
  if (!review.photos.every((photo) => !photo.isUploading)) {
    errors.photos = true
  }
  const r = formToReview(review)
  if (r.fruiting !== null && !r.observed_on) {
    errors.observed_on = true
  }
  return Object.keys(errors).length > 0 ? { review: errors } : null
}

const validateLocationForm = ({ review, ...location }, isLoggedIn) => {
  const errors = {}

  if (location.types.length === 0) {
    errors.types = true
  }

  if (!location.position) {
    errors.position = true
  }

  if (!isLoggedIn && !location['g-recaptcha-response']) {
    errors['g-recaptcha-response'] = true
  }

  if (!isEmptyReview(review)) {
    const reviewErrors = validateReviewStep(review)
    if (reviewErrors) {
      Object.assign(errors, reviewErrors)
    }
  }

  return errors
}

export const LocationForm = ({ editingId, innerRef }) => {
  const history = useAppHistory()
  const isDesktop = useIsDesktop()
  const routeLocation = useLocation()

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const {
    position,
    isLoading,
    location,
    form: reduxFormValues,
  } = useSelector((state) => state.location)

  const { typesAccess } = useSelector((state) => state.type)

  const streetViewOpen = useSelector((state) => state.panorama.streetViewOpen)

  const positionDirty =
    editingId &&
    position &&
    location &&
    (Math.abs(position.lat - location.lat) > 1e-6 ||
      Math.abs(position.lng - location.lng) > 1e-6)

  const blockedByStreetView = streetViewOpen && positionDirty

  const initialValues =
    !location || typesAccess.isNull ? {} : locationToForm(location, typesAccess)

  const isLoggedIn = useSelector((state) => !!state.auth.user)

  const mergedInitialValues = {
    ...INITIAL_LOCATION_VALUES,
    ...initialValues,
    ...reduxFormValues,
    position,
    ...(!isLoggedIn && { 'g-recaptcha-response': '' }),
  }

  const handleSubmit = (
    { 'g-recaptcha-response': recaptcha, review, ...location },
    formikProps,
  ) => {
    const locationValues = {
      'g-recaptcha-response': recaptcha,
      ...formToLocation(location),
    }

    if (!isEmptyReview(review)) {
      locationValues.review = formToReview(review)
    }

    if (editingId) {
      dispatch(
        editExistingLocation({ locationId: editingId, locationValues }),
      ).then((action) => {
        if (action.error) {
          formikProps.setSubmitting(false)
        } else {
          history.push(`/locations/${action.payload.id}`)
        }
      })
    } else {
      dispatch(addNewLocation(locationValues)).then((action) => {
        if (action.error) {
          formikProps.setSubmitting(false)
        } else {
          history.push(`/locations/${action.payload.id}/success`)
        }
      })
    }
  }

  const handleCancel = (e) => {
    e.stopPropagation()
    if (editingId) {
      history.push(`/locations/${editingId}`)
    } else {
      history.push('/map')
    }
  }

  return isLoading || typesAccess.isNull ? (
    <div>{t('layouts.loading')}</div>
  ) : (
    <StyledForm>
      <NotSignedInClickthrough
        formType={editingId ? 'edit_location' : 'add_location'}
      />
      <Formik
        validate={(values) => validateLocationForm(values, isLoggedIn)}
        initialValues={mergedInitialValues}
        onSubmit={handleSubmit}
        innerRef={innerRef}
      >
        {(formikProps) => {
          const { isSubmitting, isValid, dirty } = formikProps

          return (
            <Form>
              {routeLocation.state?.setValues &&
                Object.entries(routeLocation.state.setValues).map(
                  ([name, value]) => (
                    <SetInitialValue key={name} name={name} value={value} />
                  ),
                )}
              <LocationStep
                lat={position?.lat}
                lng={position?.lng}
                isDesktop={isDesktop}
                editingId={editingId}
                isLoading={isLoading}
              />
              {!editingId && (
                <>
                  <ReviewStep />
                </>
              )}
              {!isLoggedIn && (
                <Recaptcha centered name="g-recaptcha-response" />
              )}
              {blockedByStreetView && (
                <ErrorText>
                  {t('locations.form.street_view_position_warning')}
                </ErrorText>
              )}
              <FormButtons align={'center'}>
                <Button secondary type="button" onClick={handleCancel}>
                  {t('form.button.cancel')}
                </Button>
                <SubmitButton
                  isSubmitting={isSubmitting}
                  disabled={!isValid || !dirty || blockedByStreetView}
                />
              </FormButtons>
            </Form>
          )
        }}
      </Formik>
    </StyledForm>
  )
}
